import { useCallback, useState } from 'react'
import type { RightPanelView } from './useRightPanelState'
import type { CruiseTransportMapPoint } from '../contexts/TripPlannerEventBus'
import {
  isValidCoordinate,
  type MapCoordinate,
  type MapMarkerComponent,
} from 'tripPlanner/utils/mapMarkerTransforms'
import { SHOW_ON_MAP_ZOOM } from '../styles/layoutStyles'
import { createCruiseTransportMarkers } from '../utils/cruiseTransportUtils'

interface UseCruiseTransportMapMarkersParams {
  clearTransientMapState: () => void
  hideHotelFilter: () => void
  hideExperienceFilter: () => void
  handleExploreCategorySelect: (category: undefined) => void
  setRightPanelView: (view: RightPanelView) => void
  moveMapTo: (lat: number, lng: number, zoom: number) => void
  requestFitToCoordinates: (coordinates: Array<MapCoordinate>) => void
  clearFitBoundsRequest: () => void
}

interface UseCruiseTransportMapMarkersReturn {
  cruiseTransportMarkers: Array<MapMarkerComponent>
  clearCruiseTransportMapState: () => void
  handleCruiseTransportMapClick: (points: Array<CruiseTransportMapPoint>) => void
}

export function useCruiseTransportMapMarkers({
  clearTransientMapState,
  hideHotelFilter,
  hideExperienceFilter,
  handleExploreCategorySelect,
  setRightPanelView,
  moveMapTo,
  requestFitToCoordinates,
  clearFitBoundsRequest,
}: UseCruiseTransportMapMarkersParams): UseCruiseTransportMapMarkersReturn {
  const [cruiseTransportMarkers, setCruiseTransportMarkers] = useState<Array<MapMarkerComponent>>([])

  const clearCruiseTransportMapState = useCallback(() => {
    setCruiseTransportMarkers([])
    clearFitBoundsRequest()
  }, [clearFitBoundsRequest])

  const handleCruiseTransportMapClick = useCallback((points: Array<CruiseTransportMapPoint>) => {
    const markers = createCruiseTransportMarkers(points).filter(isValidCoordinate)
    clearTransientMapState()
    setCruiseTransportMarkers([])
    if (markers.length === 0) return

    hideHotelFilter()
    hideExperienceFilter()
    handleExploreCategorySelect(undefined)
    setCruiseTransportMarkers(markers)
    setRightPanelView('map')

    if (markers.length === 1) {
      const [marker] = markers
      clearFitBoundsRequest()
      moveMapTo(marker.lat, marker.lng, SHOW_ON_MAP_ZOOM)
      return
    }

    requestFitToCoordinates(markers)
  }, [
    clearFitBoundsRequest,
    clearTransientMapState,
    handleExploreCategorySelect,
    hideExperienceFilter,
    hideHotelFilter,
    moveMapTo,
    requestFitToCoordinates,
    setRightPanelView,
  ])

  return {
    cruiseTransportMarkers,
    clearCruiseTransportMapState,
    handleCruiseTransportMapClick,
  }
}
